import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  MessageSquare, 
  Send, 
  Upload, 
  CalendarIcon, 
  Loader2,
  CheckCircle,
  ArrowLeft,
  Globe
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Available languages
const AVAILABLE_LANGUAGES = [
  { code: 'en', name: 'English', native: 'English' },
  { code: 'es', name: 'Spanish', native: 'Español' },
  { code: 'zh', name: 'Chinese', native: '中文' },
  { code: 'vi', name: 'Vietnamese', native: 'Tiếng Việt' },
  { code: 'ko', name: 'Korean', native: '한국어' },
  { code: 'tl', name: 'Tagalog', native: 'Tagalog' },
  { code: 'fr', name: 'French', native: 'Français' },
  { code: 'pt', name: 'Portuguese', native: 'Português' },
  { code: 'ar', name: 'Arabic', native: 'العربية' },
  { code: 'ru', name: 'Russian', native: 'Русский' },
  { code: 'de', name: 'German', native: 'Deutsch' },
  { code: 'ja', name: 'Japanese', native: '日本語' },
  { code: 'hi', name: 'Hindi', native: 'हिन्दी' },
  { code: 'it', name: 'Italian', native: 'Italiano' }
];

const QUESTIONS = [
  {
    id: 'greeting',
    type: 'message',
    text: "Hi! I'm here to help you get started with your case. Were you recently involved in an accident?",
    field: 'accident_involved',
    options: [
      { label: 'Yes', value: true },
      { label: 'No', value: false }
    ]
  },
  {
    id: 'accident_type',
    type: 'select',
    text: "What type of accident was it?",
    field: 'accident_type',
    options: [
      { label: 'Car Accident', value: 'car' },
      { label: 'Truck Accident', value: 'truck' },
      { label: 'Motorcycle Accident', value: 'motorcycle' },
      { label: 'Slip & Fall', value: 'slip_fall' },
      { label: 'Workplace Injury', value: 'workplace' },
      { label: 'Pedestrian Accident', value: 'pedestrian' },
      { label: 'Other', value: 'other' }
    ]
  },
  {
    id: 'accident_date',
    type: 'date',
    text: "When did the accident happen?",
    field: 'accident_date'
  },
  {
    id: 'injured',
    type: 'yesno',
    text: "Were you injured in the accident?",
    field: 'injured'
  },
  {
    id: 'injury_description',
    type: 'text',
    text: "Please describe your injuries:",
    field: 'injury_description',
    conditional: { field: 'injured', value: true }
  },
  {
    id: 'medical_treatment',
    type: 'yesno',
    text: "Have you received any medical treatment?",
    field: 'medical_treatment'
  },
  {
    id: 'treatment_details',
    type: 'text',
    text: "What type of treatment have you received?",
    field: 'treatment_details',
    conditional: { field: 'medical_treatment', value: true }
  },
  {
    id: 'has_lawyer',
    type: 'yesno',
    text: "Do you currently have a lawyer representing you?",
    field: 'has_lawyer'
  },
  {
    id: 'file_upload',
    type: 'file',
    text: "Would you like to upload any documents? (Photos, police reports, medical records, etc.)",
    field: 'file_urls',
    optional: true
  },
  {
    id: 'client_name',
    type: 'text',
    text: "Great! Now let me get your contact information. What's your full name?",
    field: 'client_name'
  },
  {
    id: 'client_email',
    type: 'email',
    text: "What's your email address?",
    field: 'client_email'
  },
  {
    id: 'client_phone',
    type: 'phone',
    text: "And your phone number?",
    field: 'client_phone'
  },
  {
    id: 'sms_consent',
    type: 'consent',
    text: "Would you like to receive text message updates about your case?",
    field: 'sms_consent',
    consentText: "I agree to receive text messages from this law firm regarding my potential case. Message and data rates may apply. Reply STOP to opt out at any time.",
    options: [
      { label: 'Yes, send me updates', value: true },
      { label: 'No thanks', value: false }
    ]
  }
];

const ChatWidgetDemo = () => {
  const [messages, setMessages] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [inputValue, setInputValue] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [intakeResult, setIntakeResult] = useState(null);
  const [showScheduler, setShowScheduler] = useState(false);
  const [selectedConsultDate, setSelectedConsultDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [started, setStarted] = useState(false);
  const [calendlyLink, setCalendlyLink] = useState(null);
  const messagesEndRef = useRef(null);
  
  // Multi-language support
  const [showLanguageSelector, setShowLanguageSelector] = useState(true);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [selectedLanguageName, setSelectedLanguageName] = useState('English');
  const [translatedQuestions, setTranslatedQuestions] = useState(null);
  const [loadingTranslations, setLoadingTranslations] = useState(false);
  const [translationError, setTranslationError] = useState(false);
  
  // Get current questions (translated or default)
  const getQuestions = () => translatedQuestions || QUESTIONS;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  useEffect(() => {
    // Fetch Calendly link on mount
    axios.get(`${API}/calendly/scheduling-link`)
      .then(res => setCalendlyLink(res.data))
      .catch(() => { /* Calendly not configured */ });
  }, []);
  
  // Handle language selection
  const handleLanguageSelect = (langCode, langName) => {
    setSelectedLanguage(langCode);
    setSelectedLanguageName(langName);
    setShowLanguageSelector(false);
    setStarted(true);
    
    if (langCode !== 'en') {
      // Show loading message immediately
      setMessages([{ type: 'bot', text: `Loading ${langName}...` }]);
      
      // Fetch translations with cache buster
      const url = `${API}/intake-questions/translated`;
      
      axios.get(url, {
        params: { language: langCode, _t: Date.now() }
      }).then(response => {
        if (response.data.translated && response.data.questions) {
          const mapped = response.data.questions.map(q => ({
            id: q.id || q.field_name,
            type: q.type,
            text: q.question_text,
            field: q.field_name,
            options: q.options,
            conditional: q.conditional
          }));
          setTranslatedQuestions(mapped);
          // Update messages with the translated question
          const newMsg = { type: 'bot', text: mapped[0].text };
          setMessages([newMsg]);
        } else {
          setMessages([{ type: 'bot', text: QUESTIONS[0].text }]);
        }
      }).catch(() => {
        setMessages([{ type: 'bot', text: QUESTIONS[0].text }]);
      });
    } else {
      setMessages([{ type: 'bot', text: QUESTIONS[0].text }]);
    }
  };

  const startChat = () => {
    setStarted(true);
    const questions = getQuestions();
    addBotMessage(questions[0].text);
  };

  const addBotMessage = (text) => {
    setMessages(prev => [...prev, { type: 'bot', text }]);
  };

  const addUserMessage = (text) => {
    setMessages(prev => [...prev, { type: 'user', text }]);
  };

  const getCurrentQuestion = () => {
    const questions = getQuestions();
    return questions[currentStep];
  };

  const moveToNextQuestion = (updatedData = formData) => {
    const questions = getQuestions();
    let nextStep = currentStep + 1;
    
    while (nextStep < questions.length) {
      const nextQ = questions[nextStep];
      if (nextQ.conditional) {
        if (updatedData[nextQ.conditional.field] !== nextQ.conditional.value) {
          nextStep++;
          continue;
        }
      }
      break;
    }

    if (nextStep < questions.length) {
      setCurrentStep(nextStep);
      setTimeout(() => addBotMessage(questions[nextStep].text), 500);
    } else {
      submitIntake(updatedData);
    }
  };

  const handleAnswer = async (value, displayText) => {
    const question = getCurrentQuestion();
    addUserMessage(displayText || String(value));
    
    const newFormData = { ...formData, [question.field]: value };
    setFormData(newFormData);
    setInputValue('');
    
    moveToNextQuestion(newFormData);
  };

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) {
      handleAnswer([], 'No files uploaded');
      return;
    }

    setUploading(true);
    const uploadedUrls = [];

    try {
      for (const file of files) {
        const formDataObj = new FormData();
        formDataObj.append('file', file);
        
        const response = await axios.post(`${API}/upload`, formDataObj, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        uploadedUrls.push(response.data.file_url);
      }
      
      handleAnswer(uploadedUrls, `${uploadedUrls.length} file(s) uploaded`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const submitIntake = async (data) => {
    setSubmitting(true);
    
    // Multi-language processing messages
    const processingMessages = {
      en: "Thank you! I'm processing your information...",
      es: "¡Gracias! Estoy procesando su información...",
      zh: "谢谢！我正在处理您的信息...",
      vi: "Cảm ơn! Tôi đang xử lý thông tin của bạn...",
      ko: "감사합니다! 정보를 처리하고 있습니다...",
      fr: "Merci ! Je traite vos informations...",
      pt: "Obrigado! Estou processando suas informações...",
      de: "Danke! Ich verarbeite Ihre Informationen...",
      ja: "ありがとうございます！情報を処理しています..."
    };
    addBotMessage(processingMessages[selectedLanguage] || processingMessages.en);

    try {
      // Include language info in the submission
      const submissionData = {
        ...data,
        detected_language: selectedLanguage,
        detected_language_name: selectedLanguageName,
        source: 'widget'
      };
      
      const response = await axios.post(`${API}/intakes`, submissionData);
      setIntakeResult(response.data);
      setCompleted(true);
      
      // Multi-language status messages
      const statusMessages = {
        en: {
          viable: "Great news! Based on your information, it looks like an attorney may be able to help you.",
          review: "Thank you for your submission. An attorney will review your case and contact you soon.",
          unlikely: "Thank you for reaching out. An attorney will review your information."
        },
        es: {
          viable: "¡Buenas noticias! Según su información, parece que un abogado puede ayudarle.",
          review: "Gracias por su envío. Un abogado revisará su caso y se comunicará con usted pronto.",
          unlikely: "Gracias por comunicarse. Un abogado revisará su información."
        },
        zh: {
          viable: "好消息！根据您的信息，律师可能能够帮助您。",
          review: "感谢您的提交。律师将审查您的案件并尽快与您联系。",
          unlikely: "感谢您的联系。律师将审查您的信息。"
        },
        vi: {
          viable: "Tin tốt! Dựa trên thông tin của bạn, có vẻ như luật sư có thể giúp bạn.",
          review: "Cảm ơn bạn đã gửi thông tin. Luật sư sẽ xem xét trường hợp của bạn và liên hệ sớm.",
          unlikely: "Cảm ơn bạn đã liên hệ. Luật sư sẽ xem xét thông tin của bạn."
        },
        ko: {
          viable: "좋은 소식입니다! 귀하의 정보에 따르면 변호사가 도움을 드릴 수 있을 것 같습니다.",
          review: "제출해 주셔서 감사합니다. 변호사가 귀하의 사건을 검토하고 곧 연락드리겠습니다.",
          unlikely: "연락해 주셔서 감사합니다. 변호사가 귀하의 정보를 검토할 것입니다."
        },
        fr: {
          viable: "Bonne nouvelle ! D'après vos informations, il semble qu'un avocat puisse vous aider.",
          review: "Merci pour votre soumission. Un avocat examinera votre cas et vous contactera bientôt.",
          unlikely: "Merci de nous avoir contactés. Un avocat examinera vos informations."
        }
      };
      
      const langMessages = statusMessages[selectedLanguage] || statusMessages.en;
      addBotMessage(langMessages[response.data.status] || langMessages.review);
      
      if (response.data.status !== 'unlikely') {
        // Multi-language schedule prompt
        const scheduleMessages = {
          en: "Would you like to schedule a free consultation?",
          es: "¿Le gustaría programar una consulta gratuita?",
          zh: "您想预约免费咨询吗？",
          vi: "Bạn có muốn đặt lịch tư vấn miễn phí không?",
          ko: "무료 상담을 예약하시겠습니까?",
          fr: "Souhaitez-vous planifier une consultation gratuite ?",
          pt: "Gostaria de agendar uma consulta gratuita?",
          de: "Möchten Sie eine kostenlose Beratung vereinbaren?",
          ja: "無料相談のご予約はいかがですか？"
        };
        setTimeout(() => {
          addBotMessage(scheduleMessages[selectedLanguage] || scheduleMessages.en);
          setShowScheduler(true);
        }, 1000);
      }
    } catch (error) {
      console.error('Submit error:', error);
      const errorMessages = {
        en: "I'm sorry, there was an error submitting your information. Please try again.",
        es: "Lo siento, hubo un error al enviar su información. Por favor, inténtelo de nuevo.",
        zh: "抱歉，提交您的信息时出错。请重试。",
        vi: "Xin lỗi, đã xảy ra lỗi khi gửi thông tin của bạn. Vui lòng thử lại.",
        ko: "죄송합니다. 정보 제출 중 오류가 발생했습니다. 다시 시도해 주세요.",
        fr: "Désolé, une erreur s'est produite lors de l'envoi de vos informations. Veuillez réessayer."
      };
      addBotMessage(errorMessages[selectedLanguage] || errorMessages.en);
      setSubmitting(false);
    }
  };

  const fetchAvailableSlots = async (date) => {
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const response = await axios.get(`${API}/consultations/available-slots?date=${dateStr}`);
      setAvailableSlots(response.data.filter(slot => slot.available));
    } catch (error) {
      console.error('Error fetching slots:', error);
    }
  };

  const handleScheduleConsultation = async () => {
    if (!selectedConsultDate || !selectedSlot) {
      toast.error('Please select a date and time');
      return;
    }

    try {
      await axios.post(`${API}/consultations`, {
        intake_id: intakeResult.id,
        consultation_date: format(selectedConsultDate, 'yyyy-MM-dd'),
        consultation_time: selectedSlot,
        client_name: formData.client_name,
        client_email: formData.client_email,
        client_phone: formData.client_phone
      });

      setShowScheduler(false);
      addBotMessage(`Your consultation has been scheduled for ${format(selectedConsultDate, 'MMMM d, yyyy')} at ${selectedSlot}. We look forward to speaking with you!`);
      toast.success('Consultation scheduled!');
    } catch (error) {
      console.error('Schedule error:', error);
      toast.error('Failed to schedule consultation');
    }
  };

  const resetChat = () => {
    setMessages([]);
    setCurrentStep(0);
    setFormData({});
    setInputValue('');
    setSelectedDate(null);
    setCompleted(false);
    setIntakeResult(null);
    setShowScheduler(false);
    setSelectedConsultDate(null);
    setAvailableSlots([]);
    setSelectedSlot('');
    setSubmitting(false);
    setStarted(false);
  };

  const question = getCurrentQuestion();

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <Button variant="ghost" size="sm" className="text-slate-600" data-testid="back-home-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                IntakePal
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 
            className="text-3xl md:text-4xl font-bold text-slate-900 mb-4"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Intake Demo
          </h1>
          <p className="text-lg text-slate-600">
            Try our conversational intake chatbot below
          </p>
        </div>

        {/* Chat Container */}
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
          {/* Chat Header */}
          <div className="bg-slate-900 px-6 py-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold">IntakePal Assistant</h3>
              <p className="text-slate-400 text-sm">Online now</p>
            </div>
          </div>

          {/* Messages Area */}
          <div className="h-96 overflow-y-auto p-6 space-y-4 bg-slate-50">
            {/* Language Selector - only show when not started */}
            {showLanguageSelector && (
              <div className="flex flex-col items-center justify-center h-full">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                  <Globe className="w-8 h-8 text-teal-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2">Choose Your Language</h3>
                <p className="text-slate-600 text-center mb-4 text-sm">
                  Select your preferred language to continue
                </p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-48 overflow-y-auto px-2">
                  {AVAILABLE_LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageSelect(lang.code, lang.name)}
                      className="p-3 text-sm rounded-lg border border-slate-200 hover:border-teal-400 hover:bg-teal-50 transition-colors text-center"
                      data-testid={`lang-select-${lang.code}`}
                    >
                      <span className="block font-medium text-slate-700">{lang.native}</span>
                      <span className="block text-slate-400 text-xs">{lang.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Messages - show when started */}
            {started && (
              <>
                {messages.map((msg, index) => (
                  <div 
                    key={index} 
                    className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div 
                      className={`px-4 py-3 rounded-2xl max-w-[80%] ${
                        msg.type === 'user' 
                          ? 'bg-teal-500 text-white rounded-tr-sm' 
                          : 'bg-white border border-slate-200 text-slate-800 rounded-tl-sm shadow-sm'
                      }`}
                    >
                      {msg.text}
                    </div>
                  </div>
                ))}
                
                {submitting && !completed && (
                  <div className="flex justify-start">
                    <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                        <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                        <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Input Area */}
          {started && !showLanguageSelector && (
            <div className="border-t border-slate-200 p-4 bg-white">
              {completed && showScheduler ? (
                <div className="space-y-4">
                  {/* Calendly Option */}
                  {calendlyLink?.configured && calendlyLink?.event_types?.length > 0 && (
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm font-medium text-blue-900 mb-3">Schedule with Calendly:</p>
                      <div className="space-y-2">
                        {calendlyLink.event_types.map((et, idx) => (
                          <a
                            key={idx}
                            href={et.scheduling_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-200 hover:border-blue-400 transition-colors"
                            data-testid={`calendly-link-${idx}`}
                          >
                            <div>
                              <p className="font-medium text-slate-900">{et.name}</p>
                              <p className="text-xs text-slate-500">{et.duration} minutes</p>
                            </div>
                            <CalendarIcon className="w-4 h-4 text-blue-500" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Divider */}
                  {calendlyLink?.configured && (
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-slate-200" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-white px-2 text-slate-500">Or use our scheduler</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Built-in Scheduler */}
                  <div className="flex gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="flex-1 justify-start" data-testid="select-date-btn">
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          {selectedConsultDate ? format(selectedConsultDate, 'MMM d, yyyy') : 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={selectedConsultDate}
                          onSelect={(date) => {
                            setSelectedConsultDate(date);
                            if (date) fetchAvailableSlots(date);
                          }}
                          disabled={(date) => date < new Date()}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  
                  {availableSlots.length > 0 && (
                    <Select value={selectedSlot} onValueChange={setSelectedSlot}>
                      <SelectTrigger data-testid="select-time">
                        <SelectValue placeholder="Select time" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableSlots.map((slot) => (
                          <SelectItem key={slot.time} value={slot.time}>
                            {slot.time}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => setShowScheduler(false)}
                      data-testid="skip-schedule-btn"
                    >
                      Skip for now
                    </Button>
                    <Button 
                      className="flex-1 bg-teal-500 hover:bg-teal-600"
                      onClick={handleScheduleConsultation}
                      disabled={!selectedConsultDate || !selectedSlot}
                      data-testid="confirm-schedule-btn"
                    >
                      Schedule
                    </Button>
                  </div>
                </div>
              ) : completed ? (
                <div className="text-center py-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <CheckCircle className="w-6 h-6 text-teal-600" />
                  </div>
                  <p className="text-slate-600 text-sm mb-3">Thank you for your submission!</p>
                  <Button variant="outline" size="sm" onClick={resetChat} data-testid="start-new-btn">
                    Start New Inquiry
                  </Button>
                </div>
              ) : question?.type === 'yesno' || question?.id === 'greeting' ? (
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    className="flex-1 h-11"
                    onClick={() => handleAnswer(true, 'Yes')}
                    data-testid="answer-yes-btn"
                  >
                    Yes
                  </Button>
                  <Button 
                    variant="outline" 
                    className="flex-1 h-11"
                    onClick={() => handleAnswer(false, 'No')}
                    data-testid="answer-no-btn"
                  >
                    No
                  </Button>
                </div>
              ) : question?.type === 'select' ? (
                <Select onValueChange={(value) => {
                  const option = question.options.find(o => o.value === value);
                  handleAnswer(value, option?.label || value);
                }}>
                  <SelectTrigger className="h-11" data-testid="select-input">
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent>
                    {question.options.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : question?.type === 'date' ? (
                <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full h-11 justify-start" data-testid="date-picker-btn">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {selectedDate ? format(selectedDate, 'PPP') : 'Select a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => {
                        setSelectedDate(date);
                        if (date) {
                          setDatePopoverOpen(false);
                          handleAnswer(format(date, 'yyyy-MM-dd'), format(date, 'PPP'));
                        }
                      }}
                      disabled={(date) => date > new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              ) : question?.type === 'file' ? (
                <div className="space-y-2">
                  <Label htmlFor="file-upload" className="cursor-pointer">
                    <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center hover:border-teal-500 transition-colors">
                      {uploading ? (
                        <Loader2 className="w-6 h-6 text-teal-500 mx-auto animate-spin" />
                      ) : (
                        <>
                          <Upload className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                          <p className="text-sm text-slate-600">Click to upload files</p>
                        </>
                      )}
                    </div>
                    <input
                      id="file-upload"
                      type="file"
                      multiple
                      accept="image/*,.pdf,.doc,.docx"
                      className="hidden"
                      onChange={(e) => handleFileUpload(Array.from(e.target.files))}
                      disabled={uploading}
                      data-testid="file-input"
                    />
                  </Label>
                  <Button 
                    variant="ghost" 
                    className="w-full text-slate-500"
                    onClick={() => handleAnswer([], 'No files uploaded')}
                    disabled={uploading}
                    data-testid="skip-upload-btn"
                  >
                    Skip this step
                  </Button>
                </div>
              ) : question?.type === 'consent' ? (
                <div className="space-y-3">
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-xs text-slate-600 leading-relaxed">
                      {question.consentText}
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <Button 
                      variant="outline" 
                      className="flex-1 h-11 border-teal-500 text-teal-600 hover:bg-teal-50"
                      onClick={() => handleAnswer(true, 'Yes, I consent to SMS')}
                      data-testid="consent-yes-btn"
                    >
                      {question.options[0].label}
                    </Button>
                    <Button 
                      variant="outline" 
                      className="flex-1 h-11"
                      onClick={() => handleAnswer(false, 'No SMS')}
                      data-testid="consent-no-btn"
                    >
                      {question.options[1].label}
                    </Button>
                  </div>
                </div>
              ) : (
                <form 
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (inputValue.trim()) {
                      handleAnswer(inputValue.trim());
                    }
                  }}
                  className="flex gap-2"
                >
                  <Input
                    type={question?.type === 'email' ? 'email' : question?.type === 'phone' ? 'tel' : 'text'}
                    placeholder="Type your answer..."
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    className="flex-1 h-11"
                    data-testid="text-input"
                  />
                  <Button 
                    type="submit" 
                    size="icon"
                    className="h-11 w-11 bg-teal-500 hover:bg-teal-600"
                    disabled={!inputValue.trim()}
                    data-testid="send-btn"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </form>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ChatWidgetDemo;
